import { z } from "zod";

export enum ModalMode {
  Create = "create",
  Edit = "edit",
}

// Single source of truth for user roles (includes Netic-only special roles)
export const userRoleSchema = z.enum([
  "admin",
  "manager",
  "member",
  "analytics_viewer",
  "non_dashboard_user",
  "netic_view",
  "netic_ds",
]);
export type UserRole = z.infer<typeof userRoleSchema>;

/** Roles that can be assigned in User Management / CSV import. Excludes Netic-only specials. */
export const assignableUserRoleSchema = z.enum([
  "admin",
  "manager",
  "member",
  "analytics_viewer",
  "non_dashboard_user",
]);
export type AssignableUserRole = z.infer<typeof assignableUserRoleSchema>;
export const ASSIGNABLE_USER_ROLES = assignableUserRoleSchema.options;

// Tenant permission schema
export const tenantPermissionSchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  role: userRoleSchema,
});

export type TenantPermission = z.infer<typeof tenantPermissionSchema>;

// Group membership schema
export const groupMembershipSchema = z.object({
  group_id: z.string(),
  group_name: z.string(),
});

export type GroupMembership = z.infer<typeof groupMembershipSchema>;

// Updated user management schema with tenant permissions
export const userManagementAccount = z.object({
  user_id: z.string(),
  account_name: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string(),
  super_admin: z.boolean(),
  tenant_permissions: z.array(tenantPermissionSchema),
  group_memberships: z.array(groupMembershipSchema).optional(),
  // Deprecated fields (kept for backward compatibility)
  tenant_id: z.string().optional(),
  tenant_name: z.string().optional(),
  user_role: userRoleSchema.optional(),
});

export type UserManagementAccount = z.infer<typeof userManagementAccount>;

export const userManagementResponseSchema = z.array(userManagementAccount);

export const createNewUserParamsSchema = z.object({
  email: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  super_admin: z.boolean().optional().default(false),
  password: z.string().optional(),
  tenant_permissions: z.array(
    z.object({
      tenant_id: z.string(),
      role: assignableUserRoleSchema,
    })
  ),
});

export type CreateNewUserParams = z.infer<typeof createNewUserParamsSchema>;

export const updateUserParamsSchema = z.object({
  user_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  super_admin: z.boolean().optional(),
  tenant_permissions: z
    .array(
      z.object({
        tenant_id: z.string(),
        role: assignableUserRoleSchema,
      })
    )
    .optional(),
});

export type UpdateUserParams = z.infer<typeof updateUserParamsSchema>;

export const deactivateUserSchema = z.object({
  user_id: z.string().uuid(),
});

export type DeactivateUserParams = z.infer<typeof deactivateUserSchema>;

// New schema for updating a single tenant permission
export const updateTenantPermissionSchema = z.object({
  user_id: z.string(),
  tenant_id: z.string(),
  role: assignableUserRoleSchema,
});

export type UpdateTenantPermissionParams = z.infer<typeof updateTenantPermissionSchema>;

// Schema for deleting a tenant permission
export const deleteTenantPermissionSchema = z.object({
  user_id: z.string(),
  tenant_id: z.string(),
});

export type DeleteTenantPermissionParams = z.infer<typeof deleteTenantPermissionSchema>;

export const createNewUserGroupParamsSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  tenant_ids: z.array(z.string()),
  users: z.array(z.string()),
});

export type CreateNewUserGroupParams = z.infer<typeof createNewUserGroupParamsSchema>;

export const updateUserGroupParamsSchema = z.object({
  user_group_id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  tenant_ids: z.array(z.string()),
  users: z.array(z.string()),
});

export type UpdateUserGroupParams = z.infer<typeof updateUserGroupParamsSchema>;

export const deleteUserGroupParamsSchema = z.object({
  user_group_id: z.string(),
});

export type DeleteUserGroupParams = z.infer<typeof deleteUserGroupParamsSchema>;

export const userManagementUserGroupSchema = z.object({
  id: z.string(),
  last_updated: z.string().datetime(),
  name: z.string(),
  description: z.string().optional(),
  tenant_ids: z.array(z.string()),
  users: z.array(z.string()),
});
export type UserManagementUserGroup = z.infer<typeof userManagementUserGroupSchema>;

export const userManagementTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type UserManagementTenant = z.infer<typeof userManagementTenantSchema>;
